import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import AdminLayout from '../../components/Layout/AdminLayout';
import {
    UserPlus,
    Shield,
    ShieldCheck,
    User,
    Mail,
    Calendar,
    Power,
    PowerOff,
    Search,
    Eye,
    EyeOff,
    Edit,
    RefreshCw,
    MoreVertical
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AdminManagement = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creating, setCreating] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const [updating, setUpdating] = useState(false);

    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const { register: registerEdit, handleSubmit: handleSubmitEdit, formState: { errors: editErrors }, reset: resetEdit, setValue } = useForm();

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            const response = await axios.get('/api/admin/admins');
            setAdmins(response.data);
        } catch (error) {
            toast.error('Failed to load admins');
            console.error('Admins fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const onCreateSubmit = async (data) => {
        setCreating(true);

        try {
            const response = await axios.post('/api/admin/admins', data);
            toast.success(`Admin ${data.name} created successfully`);
            setAdmins([response.data, ...admins]);
            setShowCreateForm(false);
            reset();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to create admin';
            toast.error(message);
        } finally {
            setCreating(false);
        }
    };

    const toggleAdminStatus = async (adminId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'disabled' : 'active';

        try {
            const response = await axios.patch(`/api/admin/admins/${adminId}/status`, {
                status: newStatus
            });

            setAdmins(admins.map(admin =>
                admin._id === adminId ? { ...admin, status: newStatus } : admin
            ));

            toast.success(`Admin ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
        } catch (error) {
            toast.error('Failed to update admin status');
            console.error('Status update error:', error);
        }
    };

    const openEditForm = (admin) => {
        setEditingAdmin(admin);
        setValue('editName', admin.name);
        setValue('editEmail', admin.email);
        setValue('editRole', admin.role);
        setShowEditForm(true);
    };

    const onEditSubmit = async (data) => {
        setUpdating(true);

        try {
            const response = await axios.put(`/api/admin/admins/${editingAdmin._id}`, {
                name: data.editName,
                email: data.editEmail,
                role: data.editRole
            });

            setAdmins(admins.map(admin =>
                admin._id === editingAdmin._id ? response.data : admin
            ));

            toast.success('Admin updated successfully');
            setShowEditForm(false);
            setEditingAdmin(null);
            resetEdit();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to update admin';
            toast.error(message);
        } finally {
            setUpdating(false);
        }
    };

    const resetPassword = async (adminId, adminName) => {
        if (!window.confirm(`Are you sure you want to reset the password for ${adminName}? They will receive a new temporary password via email.`)) {
            return;
        }

        try {
            const response = await axios.post(`/api/admin/admins/${adminId}/reset-password`);
            toast.success(response.data.message);
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to reset password';
            toast.error(message);
        }
    };

    const getRoleIcon = (role) => {
        return role === 'super_admin' ? (
            <ShieldCheck size={16} className="text-primary" />
        ) : (
            <Shield size={16} className="text-secondary" />
        );
    };

    const getRoleText = (role) => {
        return role === 'super_admin' ? 'Super Admin' : 'Admin';
    };

    const getStatusIcon = (status) => {
        return status === 'active' ? (
            <Power size={16} className="text-success" />
        ) : (
            <PowerOff size={16} className="text-danger" />
        );
    };

    const getStatusText = (status) => {
        return status === 'active' ? 'Active' : 'Disabled';
    };

    const filteredAdmins = admins.filter(admin =>
        admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const stats = {
        total: admins.length,
        active: admins.filter(a => a.status === 'active').length,
        superAdmins: admins.filter(a => a.role === 'super_admin').length,
        regularAdmins: admins.filter(a => a.role === 'admin').length
    };

    if (loading) {
        return (
            <AdminLayout title="Admin Management">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="loading-spinner"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Admin Management">
            <div className="admin-management">
                {/* Stats Cards */}
                <div className="stats-grid">
                    <motion.div
                        className="stat-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="stat-icon total">
                            <User size={24} />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.total}</h3>
                            <p>Total Admins</p>
                        </div>
                    </motion.div>

                    <motion.div
                        className="stat-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <div className="stat-icon active">
                            <Power size={24} />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.active}</h3>
                            <p>Active</p>
                        </div>
                    </motion.div>

                    <motion.div
                        className="stat-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="stat-icon super">
                            <ShieldCheck size={24} />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.superAdmins}</h3>
                            <p>Super Admins</p>
                        </div>
                    </motion.div>

                    <motion.div
                        className="stat-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <div className="stat-icon regular">
                            <Shield size={24} />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.regularAdmins}</h3>
                            <p>Regular Admins</p>
                        </div>
                    </motion.div>
                </div>

                {/* Header Actions */}
                <div className="page-header">
                    <div className="search-box">
                        <Search size={20} />
                        <input
                            type="text"
                            placeholder="Search admins..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="header-actions">
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowCreateForm(true)}
                        >
                            <UserPlus size={20} />
                            Create Admin
                        </button>
                    </div>
                </div>

                {/* Create Admin Modal */}
                {showCreateForm && (
                    <div className="modal-overlay">
                        <motion.div
                            className="modal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                        >
                            <div className="modal-header">
                                <h3>Create New Admin</h3>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowCreateForm(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <form onSubmit={handleSubmit(onCreateSubmit)} className="modal-content">
                                <div className="form-group">
                                    <label htmlFor="name" className="form-label">Full Name</label>
                                    <input
                                        id="name"
                                        type="text"
                                        className={`form-control ${errors.name ? 'error' : ''}`}
                                        placeholder="Enter admin's full name"
                                        {...register('name', {
                                            required: 'Name is required',
                                            minLength: {
                                                value: 2,
                                                message: 'Name must be at least 2 characters'
                                            }
                                        })}
                                    />
                                    {errors.name && (
                                        <div className="error-message">{errors.name.message}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">Email Address</label>
                                    <input
                                        id="email"
                                        type="email"
                                        className={`form-control ${errors.email ? 'error' : ''}`}
                                        placeholder="Enter admin's email"
                                        {...register('email', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address'
                                            }
                                        })}
                                    />
                                    {errors.email && (
                                        <div className="error-message">{errors.email.message}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="password" className="form-label">Password</label>
                                    <div className="password-input">
                                        <input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            className={`form-control ${errors.password ? 'error' : ''}`}
                                            placeholder="Enter password"
                                            {...register('password', {
                                                required: 'Password is required',
                                                minLength: {
                                                    value: 6,
                                                    message: 'Password must be at least 6 characters'
                                                }
                                            })}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <div className="error-message">{errors.password.message}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="role" className="form-label">Role</label>
                                    <select
                                        id="role"
                                        className={`form-control ${errors.role ? 'error' : ''}`}
                                        {...register('role', {
                                            required: 'Role is required'
                                        })}
                                    >
                                        <option value="">Select Role</option>
                                        <option value="admin">Regular Admin</option>
                                        <option value="super_admin">Super Admin</option>
                                    </select>
                                    {errors.role && (
                                        <div className="error-message">{errors.role.message}</div>
                                    )}
                                </div>

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowCreateForm(false)}
                                        disabled={creating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={creating}
                                    >
                                        {creating ? (
                                            <div className="loading-spinner" style={{ width: '16px', height: '16px' }} />
                                        ) : (
                                            <>
                                                <UserPlus size={16} />
                                                Create Admin
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}

                {/* Edit Admin Modal */}
                {showEditForm && editingAdmin && (
                    <div className="modal-overlay">
                        <motion.div
                            className="modal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                        >
                            <div className="modal-header">
                                <h3>Edit Admin - {editingAdmin.name}</h3>
                                <button
                                    className="modal-close"
                                    onClick={() => {
                                        setShowEditForm(false);
                                        setEditingAdmin(null);
                                        resetEdit();
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <form onSubmit={handleSubmitEdit(onEditSubmit)} className="modal-content">
                                <div className="form-group">
                                    <label htmlFor="editName" className="form-label">Full Name</label>
                                    <input
                                        id="editName"
                                        type="text"
                                        className={`form-control ${editErrors.editName ? 'error' : ''}`}
                                        placeholder="Enter admin's full name"
                                        {...registerEdit('editName', {
                                            required: 'Name is required',
                                            minLength: {
                                                value: 2,
                                                message: 'Name must be at least 2 characters'
                                            }
                                        })}
                                    />
                                    {editErrors.editName && (
                                        <div className="error-message">{editErrors.editName.message}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="editEmail" className="form-label">Email Address</label>
                                    <input
                                        id="editEmail"
                                        type="email"
                                        className={`form-control ${editErrors.editEmail ? 'error' : ''}`}
                                        placeholder="Enter admin's email"
                                        {...registerEdit('editEmail', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address'
                                            }
                                        })}
                                    />
                                    {editErrors.editEmail && (
                                        <div className="error-message">{editErrors.editEmail.message}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="editRole" className="form-label">Role</label>
                                    <select
                                        id="editRole"
                                        className={`form-control ${editErrors.editRole ? 'error' : ''}`}
                                        {...registerEdit('editRole', {
                                            required: 'Role is required'
                                        })}
                                    >
                                        <option value="">Select Role</option>
                                        <option value="admin">Regular Admin</option>
                                        <option value="super_admin">Super Admin</option>
                                    </select>
                                    {editErrors.editRole && (
                                        <div className="error-message">{editErrors.editRole.message}</div>
                                    )}
                                </div>

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowEditForm(false);
                                            setEditingAdmin(null);
                                            resetEdit();
                                        }}
                                        disabled={updating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={updating}
                                    >
                                        {updating ? (
                                            <div className="loading-spinner" style={{ width: '16px', height: '16px' }} />
                                        ) : (
                                            <>
                                                <Edit size={16} />
                                                Update Admin
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}

                {/* Admins Table */}
                <div className="card">
                    <div className="card-header">
                        <h2 className="card-title">Administrators ({filteredAdmins.length})</h2>
                        <p className="card-subtitle">Manage administrator accounts and permissions</p>
                    </div>

                    {filteredAdmins.length > 0 ? (
                        <div className="table-container">
                            <table className="admins-table">
                                <thead>
                                    <tr>
                                        <th>Admin</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Last Login</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAdmins.map((admin, index) => (
                                        <motion.tr
                                            key={admin._id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                        >
                                            <td>
                                                <div className="admin-info">
                                                    <div className="admin-avatar">
                                                        <User size={20} />
                                                    </div>
                                                    <div className="admin-details">
                                                        <h4>{admin.name}</h4>
                                                        <p>{admin.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="role-badge">
                                                    {getRoleIcon(admin.role)}
                                                    <span>{getRoleText(admin.role)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="status-badge">
                                                    {getStatusIcon(admin.status)}
                                                    <span className={admin.status === 'active' ? 'text-success' : 'text-danger'}>
                                                        {getStatusText(admin.status)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="date-display">
                                                    <Calendar size={16} />
                                                    <span>
                                                        {admin.lastLogin
                                                            ? new Date(admin.lastLogin).toLocaleDateString()
                                                            : 'Never'
                                                        }
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="date-display">
                                                    <Calendar size={16} />
                                                    <span>{new Date(admin.createdAt).toLocaleDateString()}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn btn-sm btn-outline"
                                                        onClick={() => openEditForm(admin)}
                                                        title="Edit Admin"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-warning"
                                                        onClick={() => resetPassword(admin._id, admin.name)}
                                                        title="Reset Password"
                                                    >
                                                        <RefreshCw size={16} />
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${admin.status === 'active' ? 'btn-danger' : 'btn-success'}`}
                                                        onClick={() => toggleAdminStatus(admin._id, admin.status)}
                                                        title={admin.status === 'active' ? 'Disable Admin' : 'Enable Admin'}
                                                    >
                                                        {admin.status === 'active' ? (
                                                            <PowerOff size={16} />
                                                        ) : (
                                                            <Power size={16} />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <User size={48} className="text-muted" />
                            <h3>No admins found</h3>
                            <p>
                                {searchTerm
                                    ? 'Try adjusting your search criteria.'
                                    : 'Start by creating the first admin account.'
                                }
                            </p>
                            {!searchTerm && (
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => setShowCreateForm(true)}
                                >
                                    <UserPlus size={20} />
                                    Create First Admin
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
        .admin-management {
          max-width: 1200px;
          margin: 0 auto;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: var(--white);
          padding: 24px;
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--white);
        }

        .stat-icon.total {
          background-color: var(--primary-accent);
        }

        .stat-icon.active {
          background-color: var(--success);
        }

        .stat-icon.super {
          background-color: var(--warning);
        }

        .stat-icon.regular {
          background-color: var(--info);
        }

        .stat-content h3 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: var(--text-primary);
        }

        .stat-content p {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 4px 0 0 0;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 16px;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
        }

        .search-box input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          border: 2px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-size: 14px;
          transition: var(--transition);
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--primary-accent);
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background-color: var(--white);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-lg);
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 24px 0 24px;
          margin-bottom: 20px;
        }

        .modal-header h3 {
          margin: 0;
          color: var(--text-primary);
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: var(--text-secondary);
          cursor: pointer;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--border-radius);
          transition: var(--transition);
        }

        .modal-close:hover {
          background-color: var(--gray-100);
          color: var(--text-primary);
        }

        .modal-content {
          padding: 0 24px 24px 24px;
        }

        .password-input {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: var(--transition);
        }

        .password-toggle:hover {
          color: var(--primary-accent);
          background-color: var(--gray-100);
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--gray-200);
        }

        .table-container {
          overflow-x: auto;
        }

        .admins-table {
          width: 100%;
          border-collapse: collapse;
        }

        .admins-table th {
          text-align: left;
          padding: 16px 20px;
          background-color: var(--gray-100);
          color: var(--text-primary);
          font-weight: 600;
          font-size: 14px;
          border-bottom: 1px solid var(--gray-200);
        }

        .admins-table td {
          padding: 16px 20px;
          border-bottom: 1px solid var(--gray-200);
          vertical-align: middle;
        }

        .admin-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .admin-avatar {
          width: 40px;
          height: 40px;
          background-color: var(--primary-bg);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-accent);
        }

        .admin-details h4 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .admin-details p {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .role-badge,
        .status-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
        }

        .date-display {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-secondary);
        }

                  .action-buttons {
            display: flex;
            gap: 8px;
            align-items: center;
          }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: var(--text-primary);
        }

        .empty-state p {
          color: var(--text-secondary);
          margin: 0;
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            max-width: none;
          }

          .admins-table th,
          .admins-table td {
            padding: 12px 16px;
          }

          .admin-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .modal {
            margin: 20px;
            max-width: none;
          }
        }
      `}</style>
        </AdminLayout>
    );
};

export default AdminManagement;
